/**
 * Eye Renderer
 * Creates procedurally generated realistic eyes with uncanny valley effect
 * Features: irregular blinking, micro-saccades, pupil dilation
 */

import { CONSTANTS } from '../config.js';
import { lerp, randomRange } from './Utils.js';

export class EyeRenderer {
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.baseX = x;
        this.baseY = y;

        // Configuration
        this.size = options.size || 30;
        this.irisColor = options.irisColor || CONSTANTS.COLORS.EYE_IRIS;
        this.isUncanny = options.isUncanny !== false;

        // Container for all eye elements
        this.container = scene.add.container(x, y);

        // Eye state
        this.blinkProgress = 0; // 0 = open, 1 = closed
        this.isBlinking = false;
        this.blinkDuration = 150;
        this.blinkTimer = 0;

        // Pupil state
        this.pupilDilation = 0.5; // 0 = constricted, 1 = dilated
        this.targetPupilDilation = 0.5;
        this.pupilX = 0;
        this.pupilY = 0;
        this.targetPupilX = 0;
        this.targetPupilY = 0;

        // Saccade (micro-movement) state
        this.saccadeTimer = 0;
        this.saccadeInterval = randomRange(200, 800);

        // Blink timing
        this.nextBlinkTime = this.calculateNextBlink();
        this.lastBlinkTime = 0;

        // Bloodshot intensity (increases with dread)
        this.bloodshotIntensity = 0;

        // Create eye graphics
        this.createEye();
    }

    createEye() {
        // Sclera (white of eye)
        this.sclera = this.scene.add.graphics();
        this.container.add(this.sclera);

        // Bloodshot veins layer
        this.veins = this.scene.add.graphics();
        this.container.add(this.veins);

        // Iris
        this.iris = this.scene.add.graphics();
        this.container.add(this.iris);

        // Pupil
        this.pupil = this.scene.add.graphics();
        this.container.add(this.pupil);

        // Highlights
        this.highlights = this.scene.add.graphics();
        this.container.add(this.highlights);

        // Eyelid (for blinking)
        this.eyelidTop = this.scene.add.graphics();
        this.eyelidBottom = this.scene.add.graphics();
        this.container.add(this.eyelidTop);
        this.container.add(this.eyelidBottom);

        // Initial render
        this.render();
    }

    calculateNextBlink() {
        // Irregular intervals: 2-7 seconds
        let baseInterval = randomRange(2000, 7000);

        // 20% chance of quick follow-up blink (double-blink)
        if (this.lastBlinkTime > 0 && Math.random() < 0.2) {
            baseInterval = randomRange(200, 500);
        }

        // Uncanny effect: occasional too-long stare
        if (this.isUncanny && Math.random() < 0.1) {
            baseInterval = randomRange(8000, 12000);
        }

        return this.scene.time.now + baseInterval;
    }

    blink(duration = 150) {
        if (this.isBlinking) return;

        this.isBlinking = true;
        this.blinkDuration = duration;
        this.blinkTimer = 0;
        this.lastBlinkTime = this.scene.time.now;
    }

    update(delta) {
        const now = this.scene.time.now;

        // Check for automatic blink
        if (now >= this.nextBlinkTime && !this.isBlinking) {
            this.blink();
            this.nextBlinkTime = this.calculateNextBlink();
        }

        // Update blink animation
        if (this.isBlinking) {
            this.blinkTimer += delta;
            const halfDuration = this.blinkDuration / 2;

            if (this.blinkTimer < halfDuration) {
                // Closing
                this.blinkProgress = this.blinkTimer / halfDuration;
            } else if (this.blinkTimer < this.blinkDuration) {
                // Opening
                this.blinkProgress = 1 - ((this.blinkTimer - halfDuration) / halfDuration);
            } else {
                // Done
                this.blinkProgress = 0;
                this.isBlinking = false;
            }
        }

        // Update saccades (micro eye movements)
        this.saccadeTimer += delta;
        if (this.saccadeTimer >= this.saccadeInterval) {
            this.saccadeTimer = 0;
            this.saccadeInterval = randomRange(200, 800);

            // Small random pupil offset
            if (this.isUncanny) {
                this.targetPupilX = randomRange(-3, 3);
                this.targetPupilY = randomRange(-2, 2);
            } else {
                this.targetPupilX = randomRange(-1.5, 1.5);
                this.targetPupilY = randomRange(-1, 1);
            }
        }

        // Smooth pupil movement
        this.pupilX = lerp(this.pupilX, this.targetPupilX, 0.15);
        this.pupilY = lerp(this.pupilY, this.targetPupilY, 0.15);

        // Smooth pupil dilation
        this.pupilDilation = lerp(this.pupilDilation, this.targetPupilDilation, 0.05);

        // Re-render
        this.render();
    }

    render() {
        const size = this.size;
        const halfSize = size / 2;

        // Clear all graphics
        this.sclera.clear();
        this.veins.clear();
        this.iris.clear();
        this.pupil.clear();
        this.highlights.clear();
        this.eyelidTop.clear();
        this.eyelidBottom.clear();

        // Sclera (white of eye) - slightly off-white for realism
        this.sclera.fillStyle(CONSTANTS.COLORS.EYE_SCLERA);
        this.sclera.fillEllipse(0, 0, size, size * 0.85);

        // Shadow on sclera (depth)
        this.sclera.fillStyle(0xE0D8D0, 0.4);
        this.sclera.fillEllipse(0, -size * 0.15, size * 0.9, size * 0.3);

        // Bloodshot veins (increases with dread)
        if (this.bloodshotIntensity > 0) {
            this.veins.lineStyle(1, CONSTANTS.COLORS.EYE_BLOODSHOT, this.bloodshotIntensity * 0.6);
            // Draw random vein lines
            for (let i = 0; i < 6; i++) {
                const angle = (i / 6) * Math.PI * 2;
                const startR = size * 0.3;
                const endR = size * 0.45;
                this.veins.beginPath();
                this.veins.moveTo(Math.cos(angle) * startR, Math.sin(angle) * startR * 0.85);
                // Jagged line
                const midAngle = angle + randomRange(-0.2, 0.2);
                const midR = (startR + endR) / 2;
                this.veins.lineTo(Math.cos(midAngle) * midR, Math.sin(midAngle) * midR * 0.85);
                this.veins.lineTo(Math.cos(angle) * endR, Math.sin(angle) * endR * 0.85);
                this.veins.strokePath();
            }
        }

        // Iris
        const irisSize = size * 0.55;
        const pupilOffset = { x: this.pupilX, y: this.pupilY };

        // Iris outer ring (darker)
        this.iris.fillStyle(this.darkenColor(this.irisColor, 0.7));
        this.iris.fillCircle(pupilOffset.x, pupilOffset.y, irisSize / 2);

        // Iris main color
        this.iris.fillStyle(this.irisColor);
        this.iris.fillCircle(pupilOffset.x, pupilOffset.y, irisSize / 2 - 2);

        // Iris inner ring (lighter)
        this.iris.fillStyle(this.lightenColor(this.irisColor, 1.2), 0.5);
        this.iris.fillCircle(pupilOffset.x, pupilOffset.y, irisSize / 4);

        // Iris texture (radial lines)
        this.iris.lineStyle(0.5, this.darkenColor(this.irisColor, 0.5), 0.3);
        for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const innerR = irisSize * 0.15;
            const outerR = irisSize * 0.45;
            this.iris.beginPath();
            this.iris.moveTo(
                pupilOffset.x + Math.cos(angle) * innerR,
                pupilOffset.y + Math.sin(angle) * innerR
            );
            this.iris.lineTo(
                pupilOffset.x + Math.cos(angle) * outerR,
                pupilOffset.y + Math.sin(angle) * outerR
            );
            this.iris.strokePath();
        }

        // Pupil (dilates based on state)
        const basePupilSize = irisSize * 0.25;
        const maxPupilSize = irisSize * 0.45;
        const pupilSize = lerp(basePupilSize, maxPupilSize, this.pupilDilation);

        this.pupil.fillStyle(CONSTANTS.COLORS.EYE_PUPIL);
        this.pupil.fillCircle(pupilOffset.x, pupilOffset.y, pupilSize / 2);

        // Highlights (catchlights)
        this.highlights.fillStyle(CONSTANTS.COLORS.EYE_HIGHLIGHT, 0.9);
        this.highlights.fillCircle(
            pupilOffset.x - irisSize * 0.15,
            pupilOffset.y - irisSize * 0.15,
            size * 0.08
        );
        // Secondary smaller highlight
        this.highlights.fillStyle(CONSTANTS.COLORS.EYE_HIGHLIGHT, 0.6);
        this.highlights.fillCircle(
            pupilOffset.x + irisSize * 0.2,
            pupilOffset.y + irisSize * 0.1,
            size * 0.04
        );

        // Eyelids (for blinking)
        if (this.blinkProgress > 0) {
            const eyelidClose = this.blinkProgress * halfSize * 0.9;

            // Top eyelid
            this.eyelidTop.fillStyle(0xE8D4C4); // Skin tone
            this.eyelidTop.fillRect(-halfSize, -halfSize, size, eyelidClose + halfSize * 0.1);

            // Bottom eyelid
            this.eyelidBottom.fillStyle(0xE8D4C4);
            this.eyelidBottom.fillRect(-halfSize, halfSize * 0.85 - eyelidClose * 0.5, size, eyelidClose * 0.5 + halfSize * 0.15);

            // Eyelid crease (top)
            this.eyelidTop.lineStyle(1, 0xC0A090, 0.5);
            this.eyelidTop.beginPath();
            this.eyelidTop.moveTo(-halfSize, -halfSize + eyelidClose);
            this.eyelidTop.lineTo(halfSize, -halfSize + eyelidClose);
            this.eyelidTop.strokePath();
        }
    }

    // Color manipulation helpers
    darkenColor(color, factor) {
        const r = ((color >> 16) & 0xFF) * factor;
        const g = ((color >> 8) & 0xFF) * factor;
        const b = (color & 0xFF) * factor;
        return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
    }

    lightenColor(color, factor) {
        const r = Math.min(255, ((color >> 16) & 0xFF) * factor);
        const g = Math.min(255, ((color >> 8) & 0xFF) * factor);
        const b = Math.min(255, (color & 0xFF) * factor);
        return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b);
    }

    // Set pupil to look at a position
    lookAt(worldX, worldY) {
        const dx = worldX - (this.baseX + this.container.x);
        const dy = worldY - (this.baseY + this.container.y);
        const maxOffset = this.size * 0.1;

        this.targetPupilX = Math.max(-maxOffset, Math.min(maxOffset, dx * 0.02));
        this.targetPupilY = Math.max(-maxOffset, Math.min(maxOffset, dy * 0.02));
    }

    // Set dilation (0-1)
    setDilation(value) {
        this.targetPupilDilation = Math.max(0, Math.min(1, value));
    }

    // Set bloodshot intensity (0-1)
    setBloodshot(value) {
        this.bloodshotIntensity = Math.max(0, Math.min(1, value));
    }

    // Force a blink (for external triggers)
    forceBlink(duration = 150) {
        this.blink(duration);
    }

    destroy() {
        this.container.destroy();
    }
}
